/**
 * AP-28 Nachtrag — **Artikel → Einstellungen** (`/articles/einstellungen`).
 *
 * Nummernaufbau (N-1 bis N-4) mit Live-Vorschau und die Schwelle, ab der Bilder
 * verkleinert werden (N-14). Die Vorschau entsteht aus den Eingaben; die
 * **tatsächlich** nächste Nummer rechnet nur der Server — sie kann über der
 * Startnummer liegen, wenn es schon passende Nummern gibt (N-2).
 *
 * AP-36 Phase D: feldweise ohne Knopf. Ein geleertes Präfix nimmt das
 * Trennzeichen als Folgefeld mit (N-4: ohne Präfix kein Trennzeichen). Ändern
 * mit `articles.settings.manage` (N-17); ohne das Recht sind die Felder gesperrt.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { api } from '@/lib/axios'
import { cardTitle, fieldLabel, fieldWrapper, formCard, pageWide } from '@/lib/form-styles'
import {
  FeldStatus,
  feldKlasse,
  mitStand,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { articlesApi } from '../api/articles.api'
import { leseDezimal, zuVieleStellen } from '../lib/article-format'
import { NUMBER_SEPARATORS } from '../types/article.types'

const MB = 1024 * 1024
const START_MAX = 999_999_999

const PRAEFIX = /^[\p{L}\d]{0,10}$/u

function ganzeZahl(text: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(text.trim())) return null
  const n = Number(text.trim())
  return n >= min && n <= max ? n : null
}

export default function ArticleSettingsPage() {
  const { t } = useTranslation()
  const [daten, setDaten] = useState<Datensatz | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'articleSettings',
    bezeichnung: t('articles.settings.title'),
    daten,
    schreibe: (patch, stand) => api.patch<Datensatz>('/articles/settings', patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: '/articles/settings', method: 'PATCH' },
  })

  useEffect(() => {
    articlesApi
      .settings()
      .then(({ data }) => setDaten(data as unknown as Datensatz))
      .catch((err) => setFehler(fehlertext(err, t)))
  }, [t])

  if (!daten) {
    return (
      <div className={pageWide}>
        {fehler ? <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div> : t('common.loading')}
      </div>
    )
  }

  return (
    <div className={pageWide}>
      <div className="flex items-center gap-3">
        <Link to="/articles" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('articles.settings.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('articles.settings.subtitle')}</p>
        </div>
      </div>

      <Einstellungen stelle={stelle as Speicherstelle<unknown>} />
    </div>
  )
}

function Einstellungen({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const gesperrt = !erfuelltRecht('articles.settings.manage', user?.permissions)
  const werte = useStellenDaten(stelle)

  const praefix = useFeld<string>(stelle, 'numberPrefix', {
    label: t('articles.settings.prefix'),
    gesperrt,
    lies: (x) => x.trim(),
    pruefe: (w) => (PRAEFIX.test(w) ? null : t('articles.settingsPrefixInvalid')),
    // N-4: ohne Präfix kein Trennzeichen — geht im selben PATCH mit.
    folge: (w) => (w === '' ? { numberSeparator: '' } : {}),
  })
  const trenner = useFeld<string>(stelle, 'numberSeparator', {
    art: 'auswahl',
    label: t('articles.settings.separator'),
    gesperrt: gesperrt || !praefix.wert,
    lies: (x) => x,
  })
  const start = useFeld<number | null>(stelle, 'numberStart', {
    label: t('articles.settings.start'),
    gesperrt,
    pflicht: true,
    lies: (x) => (x.trim() === '' ? null : (ganzeZahl(x, 1, START_MAX) ?? Number.NaN)),
    schreib: (w) => (w == null ? '' : String(w)),
    pruefe: (w) => (w === null || Number.isNaN(w) ? t('articles.settingsStartInvalid') : null),
  })
  const stellen = useFeld<number | null>(stelle, 'numberDigits', {
    label: t('articles.settings.digits'),
    gesperrt,
    pflicht: true,
    lies: (x) => (x.trim() === '' ? null : (ganzeZahl(x, 1, 9) ?? Number.NaN)),
    schreib: (w) => (w == null ? '' : String(w)),
    pruefe: (w) => (w === null || Number.isNaN(w) ? t('articles.settingsDigitsInvalid') : null),
  })
  const verkleinern = useFeld<number | null>(stelle, 'imageCompressAboveBytes', {
    label: t('articles.settings.compressAbove'),
    gesperrt,
    pflicht: true,
    // Eingabe in MB mit einer Nachkommastelle, gespeichert in Bytes.
    lies: (x) => {
      const mb = leseDezimal(x)
      return mb === null ? null : Number.isNaN(mb) ? Number.NaN : Math.round(mb * MB)
    },
    schreib: (w) => (w == null ? '' : String(Math.round((w / MB) * 10) / 10).replace('.', ',')),
    pruefe: (w) => {
      const mb = w === null ? Number.NaN : w / MB
      return Number.isNaN(mb) || mb < 0.1 || mb > 25 || zuVieleStellen(Math.round(mb * 10) / 10, 1)
        ? t('articles.settingsCompressInvalid')
        : null
    },
  })

  const vorschau = (() => {
    const s = start.wert
    const d = stellen.wert
    if (s === null || d === null || Number.isNaN(s) || Number.isNaN(d)) return '—'
    const p = praefix.wert ?? ''
    return `${p}${p ? (trenner.wert ?? '') : ''}${String(s).padStart(d, '0')}`
  })()

  return (
    <div className="max-w-3xl space-y-6">
      <section className={formCard}>
        <h2 className={`mb-4 ${cardTitle}`}>{t('articles.settings.numberSection')}</h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <div className={fieldWrapper}>
            <label htmlFor="as-prefix" className={fieldLabel}>
              {t('articles.settings.prefix')}
            </label>
            <input id="as-prefix" maxLength={10} className={`${feldKlasse} font-mono`} {...praefix.props} />
            <p className="text-xs text-muted-foreground">{t('articles.settings.prefixHint')}</p>
            <FeldStatus status={praefix} />
          </div>
          <div className={fieldWrapper}>
            <label htmlFor="as-separator" className={fieldLabel}>
              {t('articles.settings.separator')}
            </label>
            <select id="as-separator" className={`${feldKlasse} font-mono`} {...trenner.props} value={praefix.wert ? trenner.props.value : ''}>
              {NUMBER_SEPARATORS.map((s) => (
                <option key={s || 'keins'} value={s}>
                  {s || t('articles.settings.separatorNone')}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{t('articles.settings.separatorHint')}</p>
            <FeldStatus status={trenner} />
          </div>
          <div className={fieldWrapper}>
            <label htmlFor="as-start" className={fieldLabel}>
              {t('articles.settings.start')}
            </label>
            <input id="as-start" inputMode="numeric" className={`${feldKlasse} !w-36 text-right tabular-nums`} {...start.props} />
            <FeldStatus status={start} />
          </div>
          <div className={fieldWrapper}>
            <label htmlFor="as-digits" className={fieldLabel}>
              {t('articles.settings.digits')}
            </label>
            <input id="as-digits" inputMode="numeric" className={`${feldKlasse} !w-20 text-right tabular-nums`} {...stellen.props} />
            <p className="text-xs text-muted-foreground">{t('articles.settings.digitsHint')}</p>
            <FeldStatus status={stellen} />
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t('articles.settings.startHint')}</p>

        <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-2 rounded-md border border-border/60 bg-background px-4 py-3">
          <div>
            <span className={fieldLabel}>{t('articles.settings.preview')}</span>
            <span className="font-mono text-lg text-foreground">{vorschau}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {t('articles.settings.nextNumber', { number: werte.nextNumber as string })}
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t('articles.settings.existingHint')}</p>
      </section>

      <section className={formCard}>
        <h2 className={`mb-4 ${cardTitle}`}>{t('articles.settings.imageSection')}</h2>
        <div className={`${fieldWrapper} max-w-xs`}>
          <label htmlFor="as-compress" className={fieldLabel}>
            {t('articles.settings.compressAbove')}
          </label>
          <input id="as-compress" inputMode="decimal" className={`${feldKlasse} !w-28 text-right tabular-nums`} {...verkleinern.props} />
          <FeldStatus status={verkleinern} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t('articles.settings.compressHint')}</p>
      </section>

      {gesperrt && <p className="text-sm text-muted-foreground">{t('articles.settings.readOnly')}</p>}
    </div>
  )
}
